import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import { styled } from '@mui/material/styles';
import { i18n } from 'i18n';
import Typography from '@mui/material/Typography';
import Actions from 'components/Actions';
import AlertText from 'components/AlertText';
import Temporal from 'components/Temporal';
import PourStatusDisplay from 'modules/project/constants/pour';
import PourDetails from 'modules/project/PourDetails';
import {
  ROUTE_PATH as PourRoutePath,
  POUR_PARAM_IDENTIFIER as pourParamIdentifier,
  ROUTE_PARAM_IDENTIFIER as projectParamIdentifier,
  STOPE_PARAM_IDENTIFIER as stopeParamIdentifier
} from 'modules/project/PourDetailsPage/constants';
import { Trans } from '@lingui/react';
import {
  CustomUnitFields,
  DisplayUnits,
  FillType,
  OptimisationTargetType,
  PourDto,
  PourStatus,
  PumpType,
  ThroughputControlType,
  UcsCoefficientsDto,
  UnitSystem,
  useDeletePourMutation,
  useMineModelRheologyDataListQuery,
  usePoursQuery,
  useRecipesQuery,
  useRestorePourMutation
} from 'providers/api';
import {
  find,
  indexBy,
  prop,
  propEq
} from 'ramda';
import React from 'react';
import { useScenario } from 'modules/project/providers/scenario';
import { usePage } from 'providers/page';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_SHORT_DATE_CONFIG, resolveTemplate } from 'utils';
import { Permission } from 'providers/authorisation';
import { useConfirmation } from 'providers/confirm';
import ProjectAuthorise from 'modules/project/ProjectAuthorise';
import ApexCharts from 'apexcharts';
import ListSkeleton from '../ListSkeleton';
import { applyRecipeModelVisuals } from '../recipeModelUtils';
import { Model } from '../../hooks/useModelState';
import { updateRecipeState } from '../../canvas/Model/updateRecipeState';
import { calculateFrictionForRoute } from '../../canvas/Model/updateFriction';
import { returnShortestRoute } from '../../MineModel/Route/utils';
import PCRoute from '../../MineModel/Route/PCRoute';

const PREFIX = 'PoursTabPanel';
const classes = {
  root: `${PREFIX}-root`,
  headerTitle: `${PREFIX}-headerTitle`,
  alignRight: `${PREFIX}-alignRight`,
};

const Root = styled('div')(() => ({
  [`&.${classes.root}`]: {
    height: 'calc(100% - 190px)',
    overflowY: 'auto',
  },

  [`& .${classes.headerTitle}`]: {
    maxWidth: '80%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  [`& .${classes.alignRight}`]: {
    textAlign: 'right',
  },
}));

interface PoursTabPanelProps {
  model: Model;
  projectId: string;
  stopeId: string;
  displayUnitPreferences: DisplayUnits[];
  unitSystemPreference: UnitSystem;
  fillType: FillType;
  throughputControlType: ThroughputControlType;
  customUnitFields: CustomUnitFields[];
  backFillPlantNodeId: string | undefined;
  isHideAbandonedPour: boolean;
  pumpType: PumpType;
}

const PoursTabPanel = ({
  model,
  projectId,
  stopeId,
  displayUnitPreferences,
  unitSystemPreference,
  fillType,
  throughputControlType,
  customUnitFields,
  backFillPlantNodeId,
  isHideAbandonedPour,
  pumpType,
}: PoursTabPanelProps) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const [editing, setEditing] = React.useState<string | false>(false);
  const restoreMutation = useRestorePourMutation();
  const {
    actions: {
      setUndoActionOverride,
      clearUndoActionOverride,
    },
  } = usePage();
  const publishUndoAction = (pour: PourDto) => {
    const handleUndo = () => {
      setUndoActionOverride((current) => (current ? {
        ...current,
        enabled: false,
        loading: true,
      } : current));

      restoreMutation.mutate(pour.entityId, {
        onSuccess: () => {
          clearUndoActionOverride();
        },
        onError: () => {
          setUndoActionOverride({
            enabled: true,
            loading: false,
            onClick: handleUndo,
          });
        },
      });
    };

    setUndoActionOverride({
      enabled: true,
      loading: false,
      onClick: handleUndo,
    });
  };
  const deleteMutation = useDeletePourMutation({
    onSuccess: (_result, pour) => {
      publishUndoAction(pour);
    },
  });
  const { activeScenarioId } = useScenario();
  // includeRockFill keeps this list in step with the stope note page, which also shows rock-fill pours —
  // without it the server silently drops them and the two views disagree on the pour count.
  const { data, isLoading } = usePoursQuery({ projectId, stopeId, scenarioId: activeScenarioId ?? undefined, includeRockFill: true }, { enabled: !!stopeId });
  const { data: recipes } = useRecipesQuery({ projectId, stopeId, scenarioId: activeScenarioId ?? undefined }, { enabled: !!stopeId });
  const pours = data ? (
    isHideAbandonedPour
      ? data.filter((x) => x.status !== PourStatus.Abandoned)
      : data
  ).slice().reverse() : [];
  const { isLoading: rheologyDataSummaryListIsLoading, data: rheologyDataSummaryList } = useMineModelRheologyDataListQuery(
    {
      mineModelId: projectId,
      rheologyDataSetIds: pours ? pours?.map((pour) => pour.rheologyDataSetId ?? '') : [],
    },
    {
      enabled: !!pours && pours.length > 0,
    },
  );

  const confirm = useConfirmation();

  const rheologyDataSummariesIndexedById = rheologyDataSummaryList ? indexBy(prop('rheologyDataSetId'), rheologyDataSummaryList) : {};

  const updateMineModelVisuals = (recipeIdParam: string) => {
    const recipe = find(propEq('entityId', recipeIdParam), recipes ?? []);
    if (!recipe) return;

    const rheologyDataSet = model.rheology?.find((dataset) => dataset.rheologyDataSetId === recipe.rheologyDataSetId);
    if (!rheologyDataSet) return;

    applyRecipeModelVisuals({ model, recipe, rheologyDataSet, fillType });
  };

  const handleDelete = (pour: PourDto) => {
    confirm({
      variant: 'danger',
      description: i18n._('This action will permanently delete the pour. Are you sure you want to continue?'),
    }).then(() => deleteMutation.mutate(pour));
  };

  const handleChange = (pour: PourDto) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setExpanded(isExpanded ? pour.entityId : false);
    if (pour.entityId === editing) setEditing(false);
    updateMineModelVisuals(pour.recipeId as string);
  };

  const captureHglPng = async (scale = 2): Promise<string | null> => {
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    const chart = ApexCharts.getChartByID('hgl-chart');
    if (!chart) return null;

    try {
      const res = await chart.dataURI({ scale });
      if (res && typeof res === 'object') {
        if ('imgURI' in res && typeof res.imgURI === 'string') return res.imgURI;
        if ('blob' in res && res.blob instanceof Blob) {
          return await new Promise<string>((resolve) => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result as string);
            fr.readAsDataURL(res.blob);
          });
        }
      }
    } catch (e) {
      try {
        const viaExec = await (ApexCharts as any).exec('hgl-chart', 'dataURI', { scale });
        if (viaExec?.imgURI) return viaExec.imgURI;
      } catch {
        return null;
      }
      return null;
    }
    return null;
  };

  const handleViewPour = async (pour: PourDto) => {
    const route = model.getSelectedRoute() ?? returnShortestRoute(model.routes) as PCRoute;
    const nominalRoutePoints = route?.routePoints ?? [];
    const hglChartUri = await captureHglPng(2);

    // Get slack/max pressures if available
    const recipe = recipes?.find((r) => r.entityId === pour.recipeId);
    const rheologyDataSet = recipe && model.rheology?.find((dataset) => dataset.rheologyDataSetId === recipe.rheologyDataSetId);

    const slackPressures: Record<string, number> = {};
    const maxPressures: Record<string, number> = {};
    const flowTargetPressures: { flowRate: number; pressures: Record<string, number> }[] = [];

    if (fillType === FillType.Paste && rheologyDataSet && route && model.recipeState) {
      // Calculate slack flow pressures
      if (model.project.showMinMaxOnGraph && recipe?.pasteSlackFlowSpecification) {
        const slackRecipeState = updateRecipeState(
          model,
          { ...model.recipeState, rheologyDataSetId: rheologyDataSet.rheologyDataSetId },
          recipe.pasteSlackFlowSpecification.massConcentration / 100,
          recipe.pasteSlackFlowSpecification.binderContent / 100,
          rheologyDataSet?.tailingsDrySolidsDensity,
          rheologyDataSet?.binderDryParticleDensity,
          rheologyDataSet?.carrierFluidDensity,
        );
        calculateFrictionForRoute(model, slackRecipeState, route, rheologyDataSet);
        route.routePoints.forEach((rp) => {
          slackPressures[rp.point.id] = rp.pressure ?? 0;
        });
      }

      // Calculate max pressure pressures
      if (model.project.showMinMaxOnGraph && recipe?.pasteMaxPressureSpecification) {
        const maxRecipeState = updateRecipeState(
          model,
          { ...model.recipeState, rheologyDataSetId: rheologyDataSet.rheologyDataSetId },
          recipe.pasteMaxPressureSpecification.massConcentration / 100,
          recipe.pasteMaxPressureSpecification.binderContent / 100,
          rheologyDataSet?.tailingsDrySolidsDensity,
          rheologyDataSet?.binderDryParticleDensity,
          rheologyDataSet?.carrierFluidDensity,
        );
        calculateFrictionForRoute(model, maxRecipeState, route, rheologyDataSet);
        route.routePoints.forEach((rp) => {
          maxPressures[rp.point.id] = rp.pressure ?? 0;
        });
      }

      // Flow rate targets: same mix as the optimised recipe, different throughput. Only pressures move.
      const optimised = recipe?.pasteOptimisedSpecification;
      if (optimised) {
        (recipe?.targetLines ?? [])
          .filter((tl) => tl.targetType === OptimisationTargetType.FlowRate && (tl.flowRateTarget ?? 0) > 0)
          .forEach((tl) => {
            const flowRate = tl.flowRateTarget as number;
            const targetRecipeState = updateRecipeState(
              model,
              {
                ...model.recipeState,
                rheologyDataSetId: rheologyDataSet.rheologyDataSetId,
                throughputControlType: ThroughputControlType.FlowRate,
                wetFlowRate: flowRate,
              },
              optimised.massConcentration / 100,
              optimised.binderContent / 100,
              rheologyDataSet?.tailingsDrySolidsDensity,
              rheologyDataSet?.binderDryParticleDensity,
              rheologyDataSet?.carrierFluidDensity,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              ThroughputControlType.FlowRate,
            );
            calculateFrictionForRoute(model, targetRecipeState, route, rheologyDataSet);
            const pressures: Record<string, number> = {};
            route.routePoints.forEach((rp) => { pressures[rp.point.id] = rp.pressure ?? 0; });
            flowTargetPressures.push({ flowRate, pressures });
          });
      }

      // Restore nominal pressures
      calculateFrictionForRoute(model, model.recipeState, route, rheologyDataSet);
    }

    // Build route points with min/max and one entry per flow rate target
    const routePointsWithMinMax = nominalRoutePoints.map((rp) => ({
      ...rp,
      minPressure: slackPressures[rp.point.id],
      maxPressure: maxPressures[rp.point.id],
      targetPressures: flowTargetPressures.map((target) => ({
        flowRate: target.flowRate,
        pressure: target.pressures[rp.point.id],
      })),
    }));

    navigate(
      resolveTemplate(PourRoutePath, {
        [projectParamIdentifier]: projectId,
        [stopeParamIdentifier]: stopeId,
        [pourParamIdentifier]: pour.entityId,
      }),
      {
        state: {
          routePoints: routePointsWithMinMax,
          hglImageDataUrl: hglChartUri ?? undefined,
        },
      },
    );
  };

  const collatedDisplayValues = (pour: PourDto) => {
    if (!rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? '']) {
      return {
        mixerCoefficients: undefined,
        specificGravity: undefined,
        heightOfCylinder: undefined,
        yieldStressA: undefined,
        yieldStressB: undefined,
        targetDaysCoefficients: undefined,
        tailingsDrySolidsDensity: undefined,
        carrierFluidDensity: undefined,
        binderDryParticleDensity: undefined,
        frictionLoss: undefined,
      };
    }

    const firstYieldStressCoefficient = rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].yieldStressCoefficients;

    return {
      mixerCoefficients: rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].mixerCoefficients,
      specificGravity: rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].tailingsDrySolidsDensity,
      heightOfCylinder: rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].heightOfCylinder,
      yieldStressA: firstYieldStressCoefficient?.coefficient1,
      yieldStressB: firstYieldStressCoefficient?.coefficient2,
      targetDaysCoefficients: rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].ucsCoefficients,
      tailingsDrySolidsDensity: rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].tailingsDrySolidsDensity,
      carrierFluidDensity: rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].carrierFluidDensity,
      binderDryParticleDensity: rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].binderDryParticleDensity,
      frictionLoss: rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? ''].frictionLossFactorOfSafety,
    };
  };

  if (isLoading || rheologyDataSummaryListIsLoading) {
    return <ListSkeleton numberOfListItems={6} />;
  }

  if (!pours) {
    return <AlertText severity="info"><Trans id="No pours have been added.">No pours have been added.</Trans></AlertText>;
  }
  return (
    <Root className={classes.root}>
      {/* View, edit and delete recipes */}
      {!rheologyDataSummaryListIsLoading && pours.map((pour, index) => (
        <Accordion key={pour.entityId} expanded={expanded === pour.entityId} onChange={handleChange(pour)}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            id={`panel-${index}-header`}
          >
            <Grid container spacing={2} wrap="wrap">
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body1" style={{ wordWrap: 'break-word' }}>{pour.reference}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body1" sx={{ overflowWrap: 'anywhere' }}>{i18n._(PourStatusDisplay[pour.status])}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="caption">
                  {pour.intendedPourDate && (
                    <Temporal temporal={pour.intendedPourDate} options={DEFAULT_SHORT_DATE_CONFIG} />
                  )}
                </Typography>
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{
              width: 1,
            }}
            >
              <Typography variant="overline">{`${i18n._('Recipe:')} ${pour.recipeReference}`}</Typography>

              {!rheologyDataSummariesIndexedById[pour.rheologyDataSetId ?? '']
                && <Alert severity="error"><Trans id="Rheology data no longer available">Rheology data no longer available</Trans></Alert>}
              <PourDetails
                pour={pour}
                displayUnitPreferences={displayUnitPreferences}
                unitSystemPreference={unitSystemPreference}
                customUnitFields={customUnitFields}
                mixerCoefficients={collatedDisplayValues(pour).mixerCoefficients}
                specificGravity={collatedDisplayValues(pour).specificGravity}
                heightOfCylinder={collatedDisplayValues(pour).heightOfCylinder}
                yieldStressA={collatedDisplayValues(pour).yieldStressA}
                yieldStressB={collatedDisplayValues(pour).yieldStressB}
                fillType={fillType}
                tailingsSolidsDensity={collatedDisplayValues(pour).tailingsDrySolidsDensity}
                carrierFluidDensity={collatedDisplayValues(pour).carrierFluidDensity}
                binderParticleDensity={collatedDisplayValues(pour).binderDryParticleDensity}
                throughputControlType={throughputControlType}
                targetDaysCoefficients={collatedDisplayValues(pour).targetDaysCoefficients as { [key: string]: UcsCoefficientsDto; }}
                backFillPlantNodeId={backFillPlantNodeId}
                pipeFrictionDataItems={pour.pipeFrictionDataItems}
                pumpType={pumpType}
                mineModelNodes={Object.values(model.nodes).map((n) => ({
                  nodeId: n.id,
                  boosterPressure: n.boosterPressure,
                }))}
                routePoints={(model.getSelectedRoute() ?? returnShortestRoute(model.routes))?.routePoints}
              />

              <Box sx={{
                mt: 1,
              }}
              >
                <Actions
                  right={[
                    [
                      'delete',
                      <ProjectAuthorise permission={Permission.ManageNodeData}>
                        <Button color="secondary" onClick={() => handleDelete(pour)}>
                          <Trans id="Delete">Delete</Trans>
                        </Button>
                      </ProjectAuthorise>,
                    ],
                    [
                      'view',
                      <Button color="secondary" onClick={() => handleViewPour(pour)}>
                        <Trans id="View">View</Trans>
                      </Button>,
                    ],
                  ]}
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Root>
  );
};

export default PoursTabPanel;
